import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { createResourceLocatorString } from '../../util/routes';

import {
  Heading,
  H2,
  H3,
  Page,
  LayoutSingleColumn,
  NamedLink,
  PrimaryButton,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import css from './AboutPage.module.css';

const HERO_PEOPLE = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=240&h=240&fit=crop&q=80',
  'https://images.unsplash.com/photo-1521119989659-a83eee488004?w=240&h=240&fit=crop&q=80',
];

const HERO_STATS = [
  { value: '1,200+', label: 'verified owners' },
  { value: '8,500+', label: 'calls booked' },
  { value: '4.9', suffix: '/5', label: 'average rating' },
];

const VALUES = [
  {
    number: '01',
    label: 'Real experience',
    description:
      "Get advice from business owners who have actually navigated the challenges you're facing — not theoretical consultants reading from a deck.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M4 7c0-1.1.9-2 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <path
          d="M8 5V3m8 2V3M4 11h16M9 15l2 2 4-4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    number: '02',
    label: 'Focused sessions',
    description:
      'Paid, time-boxed video calls keep conversations valuable. Submit questions ahead of time so every minute earns its keep.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M12 7v5l3 2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    number: '03',
    label: 'Vetted experts',
    description:
      'Every business owner is reviewed before joining. We verify their experience so you can book with confidence — no fake gurus, no resellers.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2 4 5v6c0 5.5 3.84 10.18 8 11 4.16-.82 8-5.5 8-11V5l-8-3Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="m8 12 3 3 5-6"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

export const AboutPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();

  const title = intl.formatMessage({
    id: 'AboutPage.title',
    defaultMessage: 'About — DoneThat',
  });

  const handleFindOwner = () => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <section className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroOrb} aria-hidden />
            <div className={css.heroInner}>
              <span className={css.eyebrow}>Our story</span>
              <Heading as="h1" rootClassName={css.heroHeading}>
                Connecting aspiring entrepreneurs with those who&apos;ve{' '}
                <em>been there</em>.
              </Heading>
              <p className={css.heroDescription}>
                We believe the best business advice comes from people who&apos;ve actually built
                businesses — not from generic courses or theory-heavy consultants.
              </p>

              <div className={css.heroPeople} aria-hidden>
                {HERO_PEOPLE.map((src, i) => (
                  <span
                    key={i}
                    className={css.heroPerson}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                ))}
              </div>

              <ul className={css.heroStats}>
                {HERO_STATS.map((s, i) => (
                  <li key={i} className={css.heroStat}>
                    <span className={css.heroStatValue}>
                      {s.value}
                      {s.suffix && <em>{s.suffix}</em>}
                    </span>
                    <span className={css.heroStatLabel}>{s.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ===== Story (Why We Built This) ===== */}
          <section className={css.story}>
            <div className={css.storyInner}>
              <aside className={css.storyAside}>
                <span className={css.eyebrow}>Why we built this</span>
                <H2 rootClassName={css.storyHeading}>
                  Most advice is too generic — or comes from people who haven&apos;t actually{' '}
                  <em>done it</em>.
                </H2>
              </aside>

              <div className={css.storyBody}>
                <p className={css.storyParagraphLead}>
                  <span className={css.dropCap}>S</span>tarting a business is one of the most
                  challenging things you can do. The stakes are high — financially, emotionally, and
                  personally. Yet most advice out there is either too generic to be useful, or comes
                  from people who&apos;ve never actually run a business themselves.
                </p>

                <p className={css.storyParagraph}>
                  We got tired of sifting through TikTok and Instagram, trying to piece together
                  advice from random posts and sending awkward DMs to strangers hoping they&apos;d
                  reply. We wanted something better — a straightforward way to connect with real
                  business owners and get answers to our specific questions.
                </p>

                <figure className={css.pullQuote}>
                  <span className={css.pullQuoteMark} aria-hidden>
                    &ldquo;
                  </span>
                  <blockquote className={css.pullQuoteText}>
                    The best way to learn is directly from people who&apos;ve walked the path.
                  </blockquote>
                </figure>

                <p className={css.storyParagraph}>
                  We created DoneThat because we believe the best way to learn is directly from
                  people who&apos;ve walked the path. Real business owners who&apos;ve dealt with
                  real problems — from finding their first customers to managing cash flow to hiring
                  their first employee.
                </p>

                <p className={css.storyParagraph}>
                  Whether you&apos;re just exploring an idea or ready to take the leap, DoneThat
                  connects you with someone who&apos;s been exactly where you are — and made it
                  through to the other side.
                </p>
              </div>
            </div>
          </section>

          {/* ===== Mission ===== */}
          <section className={css.mission}>
            <div className={css.missionPattern} aria-hidden />
            <div className={css.missionInner}>
              <span className={css.missionEyebrow}>Our mission</span>
              <p className={css.missionStatement}>
                To make entrepreneurship <em>more accessible</em> — by connecting those starting
                out with experienced business owners who provide honest, practical guidance.
              </p>
            </div>
          </section>

          {/* ===== Values ===== */}
          <section className={css.values}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>What we stand for</span>
              <H2 rootClassName={css.sectionHeading}>
                Three commitments — to <em>you</em>.
              </H2>
            </div>
            <div className={css.valuesGrid}>
              {VALUES.map((v, i) => (
                <article key={i} className={css.valueCard}>
                  <span className={css.valueNumber} aria-hidden>
                    {v.number}
                  </span>
                  <span className={css.valueIcon}>{v.icon}</span>
                  <H3 rootClassName={css.valueLabel}>{v.label}</H3>
                  <p className={css.valueDesc}>{v.description}</p>
                </article>
              ))}
            </div>
          </section>

          {/* ===== CTA ===== */}
          <section className={css.cta}>
            <div className={css.ctaPattern} aria-hidden />
            <div className={css.ctaInner}>
              <span className={css.ctaEyebrow}>Ready to get started?</span>
              <H2 rootClassName={css.ctaHeading}>
                Talk to someone who&apos;s <em>been there</em>.
              </H2>
              <p className={css.ctaSubtitle}>
                Browse our community of experienced business owners and book your first video call
                today.
              </p>
              <div className={css.ctaActions}>
                <PrimaryButton className={css.ctaButton} onClick={handleFindOwner}>
                  Find an owner
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </PrimaryButton>
                <NamedLink name="HowItWorksPage" className={css.ctaSecondary}>
                  See how it works
                </NamedLink>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedAboutPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <AboutPageComponent
      routeConfiguration={routeConfiguration}
      config={config}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AboutPage = compose(connect(mapStateToProps))(EnhancedAboutPage);

export default AboutPage;
